import { useCurrentUser } from "@babackapp/private-api-client/client/use-current-user";
import { useSendMfaSettingsEmailCode } from "@babackapp/private-api-client/client/use-send-mfa-settings-email-code";
import {
  useSetSuperpower,
  useSuperpowers,
} from "@babackapp/private-api-client/client/use-superpowers";
import type { SuperAdminAccountDto } from "@babackapp/private-api-client/generated/schema";
import { Button } from "@babackapp/ui/components/button";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import {
  AlertTriangle,
  Loader2,
  ShieldCheck,
  ShieldOff,
  UserPlus,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { SuperAdminDefaultShop } from "./super-admin-default-shop";

const BABACK_EMAIL_DOMAIN = "@baback.co";

const ERROR_MESSAGES: Record<string, string> = {
  AUTH_MFA_INVALID_CODE: "Code incorrect.",
  AUTH_MFA_LOCKED: "Trop d'essais : réessayez dans quelques minutes.",
  AUTH_MFA_NOT_ENABLED:
    "Activez la double authentification sur votre compte pour attribuer des accès.",
  AUTH_USER_NOT_FOUND: "Aucun compte avec cet email.",
  SUPERPOWER_EXTERNAL_ACCOUNT_CONFIRMATION_REQUIRED:
    "Confirmez l'attribution à un compte hors baback.co.",
  SUPERPOWER_AMBIGUOUS_EMAIL:
    "Plusieurs comptes utilisent cet email : impossible de choisir.",
  SUPERPOWER_LAST_ACTIVE_SUPER_ADMIN:
    "Impossible : c'est le dernier super admin avec la double authentification.",
  SUPERPOWER_TARGET_MFA_REQUIRED:
    "Ce compte doit d'abord activer sa double authentification.",
};

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  const { code, message } = error as { code?: string; message?: string };
  return (code && ERROR_MESSAGES[code]) || message || "Erreur inattendue.";
}

function isExternalEmail(email: string): boolean {
  return !email.trim().toLowerCase().endsWith(BABACK_EMAIL_DOMAIN);
}

interface PendingChange {
  email: string;
  isActivated: boolean;
}

/** Grant and revoke super admin powers, with a fresh MFA code (BAB-3035). */
export function SuperAdminsTab() {
  const { data, isPending, isError } = useSuperpowers();
  const [email, setEmail] = useState("");
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(
    null,
  );

  const onGrant = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim()) {
      setPendingChange({ email: email.trim(), isActivated: true });
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border bg-card p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold">Super admins</h2>
          <p className="text-sm text-muted-foreground">
            Seul un super admin peut attribuer ou retirer ces accès, avec un
            code de double authentification. Le compte doit avoir activé sa
            propre double authentification avant de les recevoir, et ne peut
            plus retirer sa dernière méthode ensuite. Retirer les accès
            déconnecte le compte partout (BAB-3035).
          </p>
        </div>

        <form onSubmit={onGrant} className="flex gap-2 max-w-lg">
          <Input
            type="email"
            placeholder="prenom@baback.co"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button type="submit" disabled={!email.trim()}>
            <UserPlus className="h-4 w-4" />
            Attribuer
          </Button>
        </form>

        {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
        {isError && (
          <p className="text-sm text-red-600">Chargement impossible.</p>
        )}
        {data && (
          <SuperAdminsTable
            superAdmins={data.superAdmins}
            onRevoke={(account) =>
              setPendingChange({ email: account.email, isActivated: false })
            }
          />
        )}
      </section>

      <SuperAdminDefaultShop />

      {data && data.recentChanges.length > 0 && (
        <section className="rounded-lg border bg-card p-6 space-y-3">
          <h2 className="text-base font-semibold">Historique</h2>
          <ul className="space-y-1 text-sm">
            {data.recentChanges.map((change) => (
              <li
                key={`${change.userId}-${change.createdAt}`}
                className="flex gap-2"
              >
                <span className="text-muted-foreground tabular-nums">
                  {new Date(change.createdAt).toLocaleString("fr-FR")}
                </span>
                <span>
                  {change.byUserEmail}{" "}
                  {change.action === "GRANT" ? "a attribué" : "a retiré"} les
                  accès {change.action === "GRANT" ? "à" : "de"}{" "}
                  <strong>{change.userEmail}</strong>
                  {change.isExternalAccount && " (compte hors baback.co)"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pendingChange && (
        <ConfirmSuperpowerDialog
          change={pendingChange}
          onClose={() => setPendingChange(null)}
          onDone={() => {
            setPendingChange(null);
            setEmail("");
          }}
        />
      )}
    </div>
  );
}

function SuperAdminsTable({
  superAdmins,
  onRevoke,
}: {
  superAdmins: SuperAdminAccountDto[];
  onRevoke: (account: SuperAdminAccountDto) => void;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-muted-foreground border-b">
          <th className="py-2 font-medium">Compte</th>
          <th className="py-2 font-medium">Double authentification</th>
          <th className="py-2 font-medium">Origine</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {superAdmins.map((account) => (
          <tr key={account.userId} className="border-b last:border-0">
            <td className="py-2">
              <div className="font-medium">{account.email}</div>
              {(account.firstName || account.lastName) && (
                <div className="text-muted-foreground">
                  {[account.firstName, account.lastName]
                    .filter(Boolean)
                    .join(" ")}
                </div>
              )}
            </td>
            <td className="py-2">
              {account.isMfaEnabled ? (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <ShieldCheck className="h-3.5 w-3.5" /> Activée
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <ShieldOff className="h-3.5 w-3.5" /> Absente : accès bientôt
                  coupés
                </span>
              )}
            </td>
            <td className="py-2">
              {account.hasGrantTrace ? (
                "Attribué depuis l'admin"
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" /> Hors admin (base,
                  Forest)
                </span>
              )}
            </td>
            <td className="py-2 text-right">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRevoke(account)}
              >
                Retirer
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ConfirmSuperpowerDialog({
  change,
  onClose,
  onDone,
}: {
  change: PendingChange;
  onClose: () => void;
  onDone: () => void;
}) {
  const { data: viewer } = useCurrentUser();
  const setSuperpower = useSetSuperpower();
  const sendEmailCode = useSendMfaSettingsEmailCode();
  const methods = viewer?.mfaMethods ?? [];
  // Derived until the user picks one: `/users/me` may still be loading.
  const [pickedMethod, setMethod] = useState<"totp" | "email" | null>(null);
  const method = pickedMethod ?? (methods.includes("totp") ? "totp" : "email");
  const [code, setCode] = useState("");
  const [isExternalAccountConfirmed, setIsExternalAccountConfirmed] =
    useState(false);

  const isExternal = change.isActivated && isExternalEmail(change.email);
  const canSubmit =
    code.trim().length > 0 &&
    (!isExternal || isExternalAccountConfirmed) &&
    !setSuperpower.isPending;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const proof =
      method === "totp" ? { code: code.trim() } : { emailCode: code.trim() };
    setSuperpower.mutate(
      {
        email: change.email,
        isActivated: change.isActivated,
        isExternalAccountConfirmed: isExternal
          ? isExternalAccountConfirmed
          : undefined,
        ...proof,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {change.isActivated
              ? "Attribuer les accès super admin"
              : "Retirer les accès super admin"}
          </DialogTitle>
          <DialogDescription>
            {change.isActivated ? "Attribuer à" : "Retirer à"}{" "}
            <strong>{change.email}</strong>. L'action est tracée à votre nom.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          {isExternal && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-3 text-sm">
              <p className="flex gap-2 text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Ce compte n'est pas en baback.co : il aura accès à toutes les
                boutiques de tous les clients.
              </p>
              <label className="flex items-start gap-2">
                <Checkbox
                  checked={isExternalAccountConfirmed}
                  onCheckedChange={(checked) =>
                    setIsExternalAccountConfirmed(checked === true)
                  }
                />
                <span>
                  Je confirme donner les accès super admin à un compte hors
                  Baback.
                </span>
              </label>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="superpower-code">
              {method === "totp"
                ? "Code de votre application d'authentification"
                : "Code reçu par email"}
            </Label>
            <div className="flex gap-2">
              <Input
                id="superpower-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                autoComplete="one-time-code"
                className="w-40"
              />
              {method === "email" && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={sendEmailCode.isPending}
                  onClick={() => sendEmailCode.mutate()}
                >
                  {sendEmailCode.isSuccess ? "Renvoyer" : "Envoyer le code"}
                </Button>
              )}
            </div>
            {methods.length > 1 && (
              <button
                type="button"
                className="text-sm font-medium text-violet-600"
                onClick={() => {
                  setMethod(method === "totp" ? "email" : "totp");
                  setCode("");
                }}
              >
                {method === "totp"
                  ? "Recevoir un code par email"
                  : "Utiliser l'application d'authentification"}
              </button>
            )}
          </div>

          {errorMessage(setSuperpower.error ?? sendEmailCode.error) && (
            <p className="text-sm text-red-600">
              {errorMessage(setSuperpower.error ?? sendEmailCode.error)}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant={change.isActivated ? "default" : "destructive"}
              disabled={!canSubmit}
            >
              {setSuperpower.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {change.isActivated ? "Attribuer" : "Retirer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
